/**
 * Right of access, on the profile page — `GET /api/account/export` (PIVOT-115,
 * constitution §V, GDPR Article 15). Downloads everything Pivot holds about the
 * candidate as one JSON file.
 *
 * Sits beside `DeleteAccount`, deliberately with none of its ceremony: access
 * carries no consent gate on the backend (it is how a candidate *decides*
 * whether to keep consenting) and nothing here is irreversible, so there is no
 * two-step confirmation to build.
 *
 * A plain `<a href="/api/account/export">` cannot carry the session's
 * Authorization header, so this fetches through `api/account.ts` like every
 * other authenticated call, then turns the JSON body into a browser download
 * with a throwaway `<a download>` + object URL — the standard way to save an
 * in-memory response as a file without a server redirect.
 */

import { useState } from 'react'
import { exportAccountData } from '../api/account'
import { useI18n } from '../i18n'
import { Icon } from './Icon'
import './DownloadData.css'

export function DownloadData() {
  const { t } = useI18n()
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState(false)

  const handleDownload = async () => {
    if (downloading) return
    setDownloading(true)
    setError(false)
    let data: Record<string, unknown>
    try {
      data = await exportAccountData()
    } catch {
      setError(true)
      setDownloading(false)
      return
    }
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    // Mirrors the backend's `Content-Disposition` filename
    // (`pivot-export-<date>.json`) closely enough — both are "today" at
    // request time, and a fetch response never surfaces that header's value
    // to JS anyway (`CORS-safelisted response header` rules), so this is
    // recomputed rather than read back.
    const filename = `pivot-export-${new Date().toISOString().slice(0, 10)}.json`
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
    setDownloading(false)
  }

  return (
    <section className="download-data">
      <h3>{t('profile.exportTitle')}</h3>
      <p className="download-data-body">{t('profile.exportBody')}</p>
      {error && <p className="form-error">{t('profile.exportFailed')}</p>}
      <button
        type="button"
        className="btn btn-outline"
        disabled={downloading}
        onClick={() => void handleDownload()}
      >
        {downloading ? (
          t('common.loading')
        ) : (
          <>
            <Icon name="download" />
            {t('profile.exportButton')}
          </>
        )}
      </button>
    </section>
  )
}
